import { useRef, type KeyboardEvent, type ReactNode } from 'react';

export interface TabItem {
  /** Stable identifier — matches the controlled `value`. */
  value: string;
  /** Visible tab label. */
  label: ReactNode;
}

export interface TabsProps {
  /** Ordered tab definitions. */
  items: TabItem[];
  /** Currently-selected tab value (controlled). */
  value: string;
  /** Fires with the next tab value on select. */
  onChange: (value: string) => void;
  /** Accessible name for the tablist. */
  label: string;
  className?: string;
  /** Test id — the tablist gets this; each tab gets `${dataTestId}-${value}`. */
  dataTestId?: string;
}

/**
 * Accessible tab strip — `role="tablist"` with roving-tabindex `role="tab"`
 * buttons, controlled `value`/`onChange`, and ←/→/Home/End keyboard nav.
 * Renders the strip only; the consumer owns the `role="tabpanel"` regions.
 * Used by the Edit/Preview/History strip on `/settings/role-rules`; reusable
 * for any tabbed surface. CSS namespace `pn-tabs`.
 */
export function Tabs({
  items,
  value,
  onChange,
  label,
  className,
  dataTestId
}: TabsProps) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const cls = ['pn-tabs', className].filter(Boolean).join(' ');

  const moveTo = (index: number) => {
    const clamped = (index + items.length) % items.length;
    refs.current[clamped]?.focus();
    onChange(items[clamped].value);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      moveTo(index + 1);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      moveTo(index - 1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      moveTo(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      moveTo(items.length - 1);
    }
  };

  return (
    <div
      className={cls}
      role="tablist"
      aria-label={label}
      data-testid={dataTestId}
    >
      {items.map((item, i) => {
        const selected = item.value === value;
        return (
          <button
            key={item.value}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="tab"
            className="pn-tabs-tab"
            title={typeof item.label === 'string' ? item.label : undefined}
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(item.value)}
            onKeyDown={(e) => onKeyDown(e, i)}
            data-testid={
              dataTestId ? `${dataTestId}-${item.value}` : undefined
            }
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}
